/**
 * useNotifications — polling hook para el bell de notificaciones del TopBar.
 *
 * Lambda + API Gateway no soportan SSE nativo, así que usamos polling cada
 * POLL_MS milisegundos con TanStack Query. El resultado es idéntico al UX de
 * un websocket para frecuencias de 15-30 s.
 *
 * El hook expone:
 *   - notifications: DashboardNotification[]   (últimas N del tenant)
 *   - unreadCount: number                       (no vistas desde lastSeenAt)
 *   - markAllRead()                             (actualiza lastSeenAt en localStorage)
 *   - isLoading, isError
 */

import { useQuery } from '@tanstack/react-query';
import { useState, useCallback, useEffect } from 'react';
import { fetchNotifications } from '../api/dashboard.api';
import type { DashboardNotification } from '../types/api.types';

const POLL_MS = 20_000;           // refetch every 20 s
const WINDOW_HOURS = 24;          // look back 24 h
const LS_KEY = 'agrovid.notif.lastSeenAt';

function getSince(): string {
  return new Date(Date.now() - WINDOW_HOURS * 60 * 60 * 1000).toISOString();
}

function getLastSeenAt(): string {
  return localStorage.getItem(LS_KEY) ?? new Date(0).toISOString();
}

export function useNotifications() {
  const [lastSeenAt, setLastSeenAt] = useState<string>(getLastSeenAt);

  // Re-sync lastSeenAt when storage changes (multi-tab)
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === LS_KEY && e.newValue) setLastSeenAt(e.newValue);
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const { data, isLoading, isError } = useQuery({
    queryKey:        ['notifications'],
    queryFn:         () => fetchNotifications(getSince()),
    refetchInterval: POLL_MS,
    refetchIntervalInBackground: false,
    staleTime:       POLL_MS - 1000,
  });

  const notifications: DashboardNotification[] = data?.notifications ?? [];

  const unreadCount = notifications.filter(n => n.occurredAt > lastSeenAt).length;

  const markAllRead = useCallback(() => {
    const now = new Date().toISOString();
    localStorage.setItem(LS_KEY, now);
    setLastSeenAt(now);
  }, []);

  return { notifications, unreadCount, lastSeenAt, markAllRead, isLoading, isError };
}
